// filepath: apps/web/src/admin/counterparties/api.ts
//
// FR-Group-B — typed REST client for the counterparty entity surface
// mounted by `cmd/server/wiring_directory_counterparty_handlers.go`
// and registered under `/api/people/counterparties/*` (see
// `internal/directory/counterparty_handlers.go`).
//
// Endpoints (7 total, mirroring the access_relationship pattern):
//
//   POST   /api/people/counterparties                -> createCounterparty
//   GET    /api/people/counterparties                -> listCounterparties
//   GET    /api/people/counterparties/{id}           -> getCounterparty
//   PUT    /api/people/counterparties/{id}           -> updateCounterparty
//   DELETE /api/people/counterparties/{id}           -> softDeleteCounterparty
//   GET    /api/people/counterparties/{id}/audit     -> getAuditTrailForCounterparty
//   GET    /api/people/counterparties/audit          -> listAuditTrail
//
// INV-2: server-side PII default-deny — tax_id, email, phone, address
// are returned ONLY when the caller has the
// `counterparty:read:sensitive` capability (admin role bypasses the
// default-deny). The wire shape uses `string | null` for each PII
// field so the SPA can render an em-dash or "Restricted" placeholder
// when the field is stripped.
//
// INV-5: the audit-trail row carries `version_before` (nullable on
// create) + `version_after` + `changed_fields` (names only — never
// values). The SPA MUST NOT log the audit `metadata` JSON body to
// the console.
//
// INV-8: every mutating call (POST / PUT / DELETE) MUST carry an
// `X-Idempotency-Key` header. `newIdempotencyKey()` is the canonical
// helper (the same one used by `features/records/comments/api.ts`)
// and is auto-injected by `callApi` when the caller passes one. The
// SPA is expected to generate a fresh key per user action — never
// reuse across actions (ISS-f4fa1b7b-365 fix pattern).

import { apiFetch } from "@/api";
import { ApiError } from "@/definitions/types";

/** Legal-form enum mirroring internal/directory/counterparty.go LegalForm.
 *
 * ISS-448 BA spec (note_13910): the BA's enum is the canonical
 * server-side set per internal/directory/counterparty.go:41-52
 *   sole_prop  -> LegalFormSoleProprietorship
 *   llc        -> LegalFormLLC
 *   jsc        -> LegalFormJSC
 *   plc        -> LegalFormPLC
 *   other      -> LegalFormOther
 * The pre-ISS-448 client enumerated a different 5-value set
 * (individual / sole_proprietor / partnership / corporation /
 * cooperative) which the server rejected with HTTP 400
 * (`legal_form must be one of: sole_prop, llc, jsc, plc, other`).
 * The fix below aligns the client with the server; the server
 * contract is the source of truth (INV-2 — server is the
 * single authority on the legal-form enumeration). */
export type LegalForm = "sole_prop" | "llc" | "jsc" | "plc" | "other";

export const LEGAL_FORMS: readonly LegalForm[] = [
  "sole_prop",
  "llc",
  "jsc",
  "plc",
  "other",
] as const;

/** Wire shape of a counterparty row as returned by the backend. */
export interface Counterparty {
  id: string;
  tenant_id: string;
  name: string;
  /**
   * INV-2: server-stripped when the caller lacks
   * `counterparty:read:sensitive`. The SPA renders an em-dash
   * placeholder in that case.
   */
  tax_id: string | null;
  legal_form: LegalForm;
  contact_person_id: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  status: "active" | "removed";
  /** INV-7: optimistic concurrency control token. */
  row_version: number;
  created_at: string;
  created_by_subject: string;
  updated_at: string;
  updated_by_subject: string;
  /** Soft-delete metadata (NULL on live rows). */
  removed_at: string | null;
  removed_by_subject: string | null;
}

/** Wire shape of a single audit-trail entry (sub-set of the DB row). */
export interface CounterpartyAuditEntry {
  id: string;
  kind: string;
  author_identity: string;
  version_before: number | null;
  version_after: number;
  metadata: Record<string, unknown>;
  timestamp: string;
}

/** Options for `listCounterparties` paging. */
export interface ListOpts {
  /** Filter by name (ILIKE). */
  q?: string;
  /** Filter by legal_form (exact match). */
  legal_form?: LegalForm;
  /** Filter by status (default: active). */
  status?: "active" | "removed";
  /** Page size — server caps at 200, default 50. */
  limit?: number;
  /** Page offset. */
  offset?: number;
}

/**
 * Standard error response envelope.
 *
 * ISS-448 round 2 (B2, architect note_13946): the counterparty
 * server's error envelope (per
 * `internal/directory/counterparty_handlers.go:414-423`
 * `writeCounterpartyError`) is `{ "code": "...", "message": "..." }`
 * — NOT `{ "error": "..." }`. The pre-round-2 interface had
 * `error: string`; the field was always undefined on
 * counterparty responses, so the error-message fallback
 * silently used the bare "HTTP 403" status string instead
 * of the server's diagnostic message. The fix below reads
 * the canonical `{ code, message }` shape and throws an
 * `ApiError` with the parsed `.status` + `.code` fields.
 * The other directory surfaces (e.g. /api/people) use a
 * different envelope (`{ error, code? }`); the legacy
 * `error` field is kept as a fallback for those surfaces.
 */
export interface ApiErrorBody {
  error?: string;
  message?: string;
  code?: string;
}

// ISS-377 round 2.1 (R2-B2): `callApi` is migrated to the canonical
// `apiFetch` client. The previous raw `fetch(url, init)` attached
// NO `Authorization: Bearer <access_token>` header (the Go server's
// `internal/auth.AuthMiddleware` requires the bearer at
// `auth.go:51-62`); every call silently 401'd in production.
// Routing through `apiFetch` attaches the bearer via the registered
// OIDC accessor and runs the 401 → silent-refresh → retry chain.
// The static-guard forbidlist
// (`apps/web/src/test/noRawApiFetchForbidlist.ts`) now requires
// every /api call in apps/web/src/** to go through this client.

/** Input for `createCounterparty` (no id / row_version / audit fields). */
export interface CreateCounterpartyInput {
  name: string;
  tax_id?: string;
  legal_form: LegalForm;
  contact_person_id?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
}

/** Input for `updateCounterparty` — OCC token is mandatory (INV-7). */
export interface UpdateCounterpartyInput {
  name?: string;
  tax_id?: string | null;
  /**
   * ISS-448 BA spec: clearing tax_id on edit sends the
   * `clear_tax_id` flag (NOT an empty string). The server's
   * update handler at
   * internal/directory/counterparty_handlers.go uses the
   * flag to explicitly null the tax_id column when set
   * to true. The flag is independent of the `tax_id` value
   * (the BA spec's "the flag is independent of the input
   * value" requirement).
   */
  clear_tax_id?: boolean;
  legal_form?: LegalForm;
  // ISS-448 BA spec: contact_person_id is OUT of v1 (needs a
  // person picker; BA deferred to a separate FR). The PUT
  // envelope no longer carries contact_person_id; the form
  // does not surface a UI input.
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  /**
   * INV-7: row_version echoed back from the GET response.
   *
   * ISS-448 round 2 (B1, architect note_13946): the wire field
   * name is `row_version` (per
   * `internal/directory/counterparty_handlers.go:142` — the
   * `updateCounterpartyReq` struct's JSON tag is
   * `json:"row_version"`). The pre-round-2 client sent
   * `expected_row_version`, which the server decoded as 0
   * (the field is missing in the body) and rejected with
   * HTTP 400 (`row_version is required and must be > 0`),
   * so every edit failed before reaching the service. The
   * fix is the client field name; the server is the source
   * of truth. The BA spec's "row_version conflict" naming
   * applies to the error MESSAGES, not the wire field.
   */
  row_version: number;
}

/**
 * Generate a v4 UUID via crypto.randomUUID() with a Date.now fallback.
 * Mirror of the comments-client helper.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

interface FetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  idempotencyKey?: string;
}

async function callApi<T>(url: string, opts: FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  // ISS-377 round 2.1 (R2-B2): routed through `apiFetch`. The
  // explicit-string `idempotencyKey` is forwarded (the dev-only
  // duplicate-key warning fires only if the same key is reused
  // within a session, which the tests do not do).
  const resp = await apiFetch(url, {
    method: opts.method ?? "GET",
    init,
    idempotencyKey: opts.idempotencyKey,
  });
  if (!resp.ok) {
    // ISS-448 round 2 (B2): the error path throws the typed
    // `ApiError` (with `.status` + `.code` + `.message` fields)
    // instead of a bare `Error(msg)`. The classifiers in
    // new.tsx + edit.tsx + list.tsx check the typed fields
    // (`e.status`, `e.code`), not string substrings, so the
    // previous stringly-typed throws (which produced bare
    // "HTTP 403" / "HTTP 409" messages) were unreliable. The
    // server's response body is parsed as the canonical
    // `{ code, message }` envelope (per
    // `internal/directory/counterparty_handlers.go:414-423`
    // `writeCounterpartyError`); the legacy `error` field is
    // kept as a fallback for the /api/people-style envelopes
    // that other directory surfaces use.
    let code = "";
    let message = `HTTP ${resp.status}`;
    try {
      const errBody = (await resp.json()) as ApiErrorBody;
      if (errBody && typeof errBody.code === "string") {
        code = errBody.code;
      }
      if (errBody && typeof errBody.message === "string") {
        message = errBody.message;
      } else if (errBody && typeof errBody.error === "string") {
        message = errBody.error;
      }
    } catch {
      // Body is not JSON — fall back to the status string.
    }
    throw new ApiError(message, resp.status, code);
  }
  // 204 No Content (DELETE success).
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  return (await resp.json()) as T;
}

function buildListQuery(opts?: ListOpts): string {
  if (!opts) return "";
  const params = new URLSearchParams();
  if (opts.q) params.set("q", opts.q);
  if (opts.legal_form) params.set("legal_form", opts.legal_form);
  if (opts.status) params.set("status", opts.status);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

const BASE = "/api/counterparties";

/**
 * POST /api/people/counterparties
 * Returns 201 + the created row (with row_version=1).
 *
 * INV-8: caller MUST supply `idempotencyKey`.
 */
export async function createCounterparty(
  input: CreateCounterpartyInput,
  idempotencyKey: string,
): Promise<Counterparty> {
  return callApi<Counterparty>(BASE, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

/**
 * GET /api/people/counterparties
 * Returns the list (default filter: status=active, limit=50).
 *
 * Wire shape: the handler at internal/directory/counterparty_handlers.go:342
 * uses `authorization.WriteStrippedList(..., "records")`, so the actual
 * envelope is `{ records: Counterparty[], total: number }` (the canonical
 * `Authorize.WriteStrippedList` shape across the directory package —
 * also used by /api/people, /api/people/audit, /api/directory/*, etc.).
 * The pre-fix client (see GitLab issue 443) expected
 * `{ counterparties: Counterparty[] }` (an older singular envelope)
 * and crashed at runtime with a `TypeError: cannot read properties
 * of undefined (reading 'counterparties')` when the SPA mounted
 * /admin/counterparties. The fix below reads the canonical
 * `{ records }` envelope; a legacy `{ counterparties }` fallback
 * is kept for any third-party fixtures (e.g. older msw handlers
 * and the api.test.ts legacy assertion) so we do not break
 * existing test plumbing.
 *
 * INV-2: rows are server-stripped — PII fields may be null for
 * non-admin callers.
 */
export async function listCounterparties(
  opts?: ListOpts,
): Promise<Counterparty[]> {
  const url = `${BASE}${buildListQuery(opts)}`;
  // The `unknown` cast is intentional: the wire envelope has
  // three accepted shapes (canonical `{ records, total }` per
  // authorization.WriteStrippedList, legacy `{ counterparties }`,
  // and a bare array). We narrow with a typeof/Array.isArray ladder
  // below so the per-shape property accesses don't trip TS's
  // discriminated-union narrowing.
  const body = (await callApi<unknown>(url)) as
    | {
        records?: Counterparty[];
        total?: number;
        counterparties?: Counterparty[];
      }
    | Counterparty[];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.records)) return body.records;
  if (Array.isArray(body.counterparties)) return body.counterparties;
  // Unknown envelope shape — surface as an empty list rather than
  // crashing the render. The fetch-failure path below catches real
  // network errors; this branch is the rare "server shipped a new
  // shape we haven't taught the client yet" case.
  return [];
}

/**
 * GET /api/people/counterparties/{id}
 * Returns 404 when the row doesn't exist or has been removed.
 */
export async function getCounterparty(id: string): Promise<Counterparty> {
  return callApi<Counterparty>(`${BASE}/${encodeURIComponent(id)}`);
}

/**
 * PUT /api/people/counterparties/{id}
 * Returns 200 + the updated row, or 409 on row_version mismatch (INV-7).
 *
 * INV-8: caller MUST supply `idempotencyKey`.
 */
export async function updateCounterparty(
  id: string,
  input: UpdateCounterpartyInput,
  idempotencyKey: string,
): Promise<Counterparty> {
  return callApi<Counterparty>(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: input,
    idempotencyKey,
  });
}

/**
 * DELETE /api/people/counterparties/{id}
 * Soft-delete (status='removed'). Returns 204 No Content on success,
 * 404 on not-found, 409 on row_version mismatch.
 *
 * INV-8: caller MUST supply `idempotencyKey`.
 */
export async function softDeleteCounterparty(
  id: string,
  expectedRowVersion: number,
  idempotencyKey: string,
): Promise<void> {
  return callApi<void>(`${BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: { expected_row_version: expectedRowVersion },
    idempotencyKey,
  });
}

/**
 * GET /api/people/counterparties/{id}/audit
 * Returns the audit trail for one counterparty, newest first.
 *
 * ISS-482 (BA UAT-35 re-capture, 2026-10-19): the server's
 * audit endpoint returned `null` for a freshly-provisioned
 * counterparty (the audit log is empty, but the JSON body
 * is `null`, not `{ entries: [] }`). The previous
 * `Array.isArray(body) ? body : body.entries` unwrap
 * crashed on `null` (no `.entries` property → `TypeError:
 * Cannot read properties of null`). The fix below returns
 * `[]` for any unrecognised shape, matching the same
 * defensive pattern `listAuditTrail` now uses.
 */
export async function getAuditTrailForCounterparty(
  id: string,
  opts?: { limit?: number; offset?: number; kind?: string },
): Promise<CounterpartyAuditEntry[]> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  if (opts?.kind) params.set("kind", opts.kind);
  const qs = params.toString();
  const url = `${BASE}/${encodeURIComponent(id)}/audit${qs ? `?${qs}` : ""}`;
  const body = await callApi<
    { entries: CounterpartyAuditEntry[] } | CounterpartyAuditEntry[]
  >(url);
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.entries)) return body.entries;
  // ISS-482: the server returned `null` (or some other
  // unrecognised shape) for a freshly-provisioned counterparty
  // with no audit entries. Return an empty array so the
  // detail screen renders the empty-state placeholder
  // (`audit.length === 0`) instead of crashing on
  // `audit.length`.
  return [];
}

/**
 * GET /api/people/counterparties/audit
 * Returns the tenant-wide audit trail across all counterparties.
 *
 * ISS-482 (BA UAT-35 re-capture, 2026-10-19): same defensive
 * pattern as `getAuditTrailForCounterparty` — the previous
 * `Array.isArray(body) ? body : body.entries` unwrap crashed
 * when the server returned `null` (an empty tenant log). The
 * fix returns `[]` for any unrecognised shape so callers
 * never get `undefined` back.
 */
export async function listAuditTrail(opts?: {
  limit?: number;
  offset?: number;
  kind?: string;
}): Promise<CounterpartyAuditEntry[]> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  if (opts?.kind) params.set("kind", opts.kind);
  const qs = params.toString();
  const url = `${BASE}/audit${qs ? `?${qs}` : ""}`;
  const body = await callApi<
    { entries: CounterpartyAuditEntry[] } | CounterpartyAuditEntry[]
  >(url);
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.entries)) return body.entries;
  // ISS-482: defensive — server may return `null` for an
  // empty tenant log. Return `[]` so callers never crash.
  return [];
}
